import { defineStore } from 'pinia';
import { useTrialFundStore } from '@/stores/trialFund';
import { getUserId } from '@/utils/auth-token';

const DONE_PREFIX = 'ds-jackpot-guide-done:v2:';

/** @typedef {'join' | 'pick' | 'joinNow' | 'pay' | ''} JackpotGuideStep */

function doneStorageKey() {
  const uid = String(getUserId() || '').trim() || 'guest';
  return `${DONE_PREFIX}${uid}`;
}

function readDone() {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(doneStorageKey()) === '1';
  } catch {
    return false;
  }
}

function writeDone() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(doneStorageKey(), '1');
  } catch {
    /* ignore quota */
  }
}

export const useJackpotGuideStore = defineStore('jackpotGuide', {
  state: () => ({
    open: false,
    /** @type {JackpotGuideStep} */
    step: '',
    /** pick 步已选号，等点底部「立即参与」 */
    pickReady: false,
    /** 本账号已完成或跳过引导 */
    done: readDone(),
    /** 当前页不会再开引导（没有可引导的进行中场次） */
    waitCancelled: false,
  }),
  getters: {
    active: (state) => state.open && Boolean(state.step),
  },
  actions: {
    /**
     * 首次自动开引导。已完成、福利盘引导进行中、或已经打开时不抢。
     * @param {'join' | 'pick'} step
     */
    tryStart(step) {
      if (this.open) return false;
      if (this.done || readDone()) {
        this.done = true;
        return false;
      }
      if (useTrialFundStore().guideActive) return false;
      this.waitCancelled = false;
      this.open = true;
      this.step = step;
      this.pickReady = false;
      return true;
    },

    /** 列表已就绪但不会进入引导，放开被压住的其它弹窗 */
    cancelWait() {
      if (this.open || this.done) return;
      this.waitCancelled = true;
    },

    advanceFromJoin() {
      if (!this.open || this.step !== 'join') return;
      this.step = 'pick';
      this.pickReady = false;
    },

    /** 已选号，最后一步改指底部「立即参与」，等用户点击 */
    advanceToJoinNow() {
      if (!this.open || this.step === 'joinNow') return;
      if (this.step !== 'pick' && this.step !== 'pay') return;
      this.step = 'joinNow';
      this.pickReady = false;
    },

    /** 选号被清空，从「立即参与」退回选号 */
    backToPickFromJoinNow() {
      if (!this.open || this.step !== 'joinNow') return;
      this.step = 'pick';
      this.pickReady = false;
    },

    advanceFromPick() {
      if (!this.open || this.step !== 'pick') return;
      this.step = 'pay';
      this.pickReady = false;
    },

    backToPick() {
      if (!this.open || this.step !== 'pay') return;
      this.step = 'pick';
      this.pickReady = false;
    },

    rewindToJoin() {
      if (!this.open) return;
      if (this.step !== 'pick' && this.step !== 'pay' && this.step !== 'joinNow') return;
      this.step = 'join';
      this.pickReady = false;
    },

    finish() {
      if (!this.open) return;
      writeDone();
      this.done = true;
      this.waitCancelled = false;
      this.open = false;
      this.step = '';
      this.pickReady = false;
    },

    closeWithoutDone() {
      this.open = false;
      this.step = '';
      this.pickReady = false;
    },
  },
});
